'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Bot, User, Loader2, Maximize2, Minimize2, Copy, Check } from 'lucide-react';

interface Message {
    role: 'user' | 'assistant';
    content: string;
}

let openBotFn: (() => void) | null = null;
export function openConsultationBot() {
    openBotFn?.();
}

export function ConsultationBot() {
    const [isOpen, setIsOpen] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const chatRef = useRef<HTMLDivElement>(null);

    const GREETING = '안녕하세요! Desinsight AI 상담 어시스턴트입니다.\n\n서비스, 비용, 구축 기간 등 궁금한 점을 자유롭게 질문해 주세요.\n\n⚠️ (RAG 데이터 보호를 위해 첫 질의 시 접근 비밀번호를 입력해야 합니다)';

    useEffect(() => {
        openBotFn = () => {
            setIsOpen(true);
            if (messages.length === 0) {
                setMessages([{ role: 'assistant', content: GREETING }]);
            }
        };
        return () => { openBotFn = null; };
    }, [messages.length]);

    useEffect(() => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [messages]);

    const sendMessage = async () => {
        if (!input.trim() || isLoading) return;
        const userMsg = input.trim();
        setInput('');

        const newMessages: Message[] = [...messages, { role: 'user', content: userMsg }];
        setMessages([...newMessages, { role: 'assistant', content: '' }]);
        setIsLoading(true);

        let authorizedNow = isAuthorized;
        if (!isAuthorized) {
            const ACCESS_PASSWORD = process.env.NEXT_PUBLIC_BOT_PASSWORD || '';
            if (ACCESS_PASSWORD && userMsg.trim() === ACCESS_PASSWORD) {
                setIsAuthorized(true);
                authorizedNow = true;
                setMessages(prev => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: 'assistant', content: '✅ 인증이 완료되었습니다. 이제 사내 NAS 연동 및 RAG 질의 기능을 제한 없이 사용할 수 있습니다.' };
                    return updated;
                });
                setIsLoading(false);
                return;
            }
        }

        try {
            // API에 보낼 메시지 (greeting 제외, user/assistant만)
            const apiMessages = newMessages
                .filter((_, i) => i > 0 || newMessages[0].role === 'user')
                .map(m => ({ role: m.role, content: m.content }));
            // greeting이 첫 메시지면 제거 (system이 아니므로)
            if (apiMessages.length > 0 && apiMessages[0].role === 'assistant') {
                apiMessages.shift();
            }

            const res = await fetch('/api/omni-chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    messages: apiMessages,
                    metadata: { source: 'consultation-bot', isAuthorized: authorizedNow }
                }),
            });

            const data = await res.json();
            
            if (data.answer || data.reply || data.response) {
                const text = data.answer || data.reply || data.response;
                setMessages(prev => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: 'assistant', content: text };
                    return updated;
                });
            } else if (data.success && data.message) {
                // 백업용(레거시 포맷)
                setMessages(prev => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: 'assistant', content: data.message.content };
                    return updated;
                });
            } else {
                throw new Error(data.error || 'Failed to communicate with Omni Gateway');
            }
        } catch {
            setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                    role: 'assistant',
                    content: '죄송합니다, 일시적인 오류가 발생했습니다.\nceo@desinsight.com으로 직접 문의해 주세요.'
                };
                return updated;
            });
        }
        setIsLoading(false);
    };

    const handleReset = () => {
        setMessages([{ role: 'assistant', content: GREETING }]);
        setInput('');
    };

    const handleCopy = useCallback((text: string, idx: number) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedIdx(idx);
            setTimeout(() => setCopiedIdx(null), 2000);
        });
    }, []);

    const toggleFullscreen = () => setIsFullscreen(prev => !prev);

    const QUICK_QUESTIONS = [
        'RAG 솔루션 비용이 궁금해요',
        '구축 기간은 얼마나 걸리나요?',
        'ChatGPT랑 뭐가 달라요?',
        '데이터 보안은 어떻게 되나요?',
        'CXD Gene이 뭔가요?',
        '무료 상담 신청하고 싶어요',
    ];

    return (
        <>
            <motion.button
                onClick={() => { setIsOpen(true); if (messages.length === 0) setMessages([{ role: 'assistant', content: GREETING }]); }}
                className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-xl shadow-blue-600/30 flex items-center justify-center hover:scale-110 transition-transform"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                aria-label="AI 상담 열기"
            >
                <Bot className="w-6 h-6" />
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className={`fixed z-50 bg-gray-900 border border-white/10 shadow-2xl shadow-black/50 overflow-hidden flex flex-col transition-all duration-300 ${isFullscreen
                                ? 'inset-4 rounded-2xl'
                                : 'bottom-24 right-6 w-[400px] max-w-[calc(100vw-48px)] rounded-2xl'
                            }`}
                        style={isFullscreen ? undefined : { height: '600px' }}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                                    <Bot className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-white font-semibold text-sm">Desinsight AI 상담</h3>
                                    <p className="text-white/60 text-[10px]">Claude AI 기반 실시간 상담</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={handleReset} className="text-white/40 hover:text-white text-[10px] px-2 py-1 border border-white/20 rounded-full transition-colors">
                                    새 대화
                                </button>
                                <button onClick={toggleFullscreen} className="text-white/60 hover:text-white transition-colors" title={isFullscreen ? '축소' : '전체화면'}>
                                    {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                                </button>
                                <button onClick={() => { setIsOpen(false); setIsFullscreen(false); }} className="text-white/60 hover:text-white transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Chat */}
                        <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                            {messages.map((msg, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}
                                >
                                    {msg.role === 'assistant' && (
                                        <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                                            <Bot className="w-3.5 h-3.5 text-blue-400" />
                                        </div>
                                    )}
                                    <div className="group relative">
                                        <div className={`${isFullscreen ? 'max-w-[70%]' : 'max-w-[80%]'} px-3.5 py-2.5 rounded-2xl ${isFullscreen ? 'text-sm' : 'text-xs'} leading-relaxed whitespace-pre-line ${msg.role === 'user'
                                                ? 'bg-blue-600 text-white rounded-br-md'
                                                : 'bg-white/5 text-gray-200 rounded-bl-md border border-white/5'
                                            }`}>
                                            {msg.content || (isLoading && i === messages.length - 1 ? (
                                                <span className="flex gap-1">
                                                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" />
                                                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                                </span>
                                            ) : '')}
                                        </div>
                                        {msg.content && msg.role === 'assistant' && (
                                            <button
                                                onClick={() => handleCopy(msg.content, i)}
                                                className="absolute -bottom-5 left-9 opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-gray-300 flex items-center gap-1 text-[10px]"
                                                title="복사"
                                            >
                                                {copiedIdx === i ? <><Check className="w-3 h-3 text-green-400" /> 복사됨</> : <><Copy className="w-3 h-3" /> 복사</>}
                                            </button>
                                        )}
                                    </div>
                                    {msg.role === 'user' && (
                                        <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                                            <User className="w-3.5 h-3.5 text-purple-400" />
                                        </div>
                                    )}
                                </motion.div>
                            ))}

                            {/* Quick questions (only on first greeting) */}
                            {messages.length === 1 && messages[0].role === 'assistant' && (
                                <div className="space-y-1.5 pt-2">
                                    <div className="text-[10px] text-gray-500 px-1">자주 묻는 질문:</div>
                                    {QUICK_QUESTIONS.map(q => (
                                        <button
                                            key={q}
                                            onClick={() => { setInput(q); }}
                                            className="block w-full text-left px-3 py-2 bg-white/5 hover:bg-blue-500/10 border border-white/10 hover:border-blue-500/20 rounded-xl text-[11px] text-gray-400 hover:text-white transition-all"
                                        >
                                            {q}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Input */}
                        <div className="p-3 border-t border-white/5 flex-shrink-0">
                            <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex gap-2">
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="서비스, 비용, 기술에 대해 물어보세요..."
                                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-gray-500 outline-none focus:border-blue-500/50 transition-colors"
                                    disabled={isLoading}
                                    autoFocus
                                />
                                <button
                                    type="submit"
                                    disabled={isLoading || !input.trim()}
                                    className="px-3 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-30 rounded-xl transition-colors"
                                >
                                    {isLoading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
                                </button>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
